import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import SubdomainRouter from './components/SubdomainRouter';
import InactivityHandler from './components/InactivityHandler';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Pages from './pages/Pages';
import PageEditor from './pages/PageEditor';
import Newsletters from './pages/Newsletters';
import AiAssistant from './pages/AiAssistant';
import AiBotsAdmin from './pages/AiBotsAdmin';
import TaptileApp from './pages/TaptileApp';
import StripePayments from './pages/StripePayments';
import NewsletterEditor from './pages/NewsletterEditor';
import MediaLibrary from './pages/MediaLibrary';
import Team from './pages/Team';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';
import GoLiveRequests from './pages/GoLiveRequests';
import PlanLimits from './pages/PlanLimits';
import Marketplace from './pages/Marketplace';
import Settings from './pages/Settings';
import ThemeSettings from './pages/ThemeSettings';
import DomainSettings from './pages/DomainSettings';
import SecuritySettings from './pages/SecuritySettings';
import APIKeys from './pages/APIKeys';
import ComplianceDocuments from './pages/ComplianceDocuments';
import HelpTutorials from './pages/HelpTutorials';
import NavigationManager from './pages/NavigationManager';
import Templates from './pages/Templates';
import APIDocs from './pages/APIDocs';
import FormBuilder from './pages/FormBuilder';
import FormEditor from './pages/FormEditor';
import FormSubmissions from './pages/FormSubmissions';
import PreviewPage from './pages/PreviewPage';
import ArticlePreviewPage from './pages/ArticlePreviewPage';
import Articles from './pages/Articles';
import ArticleEditor from './pages/ArticleEditor';
import Redirects from './pages/Redirects';
import LandingPage from './pages/LandingPage';
import LandingPageEditor from './pages/LandingPageEditor';
import TenantLandingPage from './pages/TenantLandingPage';
import PublicTenantLandingPage from './pages/PublicTenantLandingPage';
import PublicSitePage from './pages/PublicSitePage';
import PaymentSuccess from './pages/PaymentSuccess';
import WebhookLogs from './pages/WebhookLogs';
import ResellerManagement from './pages/ResellerManagement';
import ResellerDashboard from './pages/ResellerDashboard';
import ResellerBilling from './pages/ResellerBilling';
import ResellerApplication from './pages/ResellerApplication';
import AffiliateApplication from './pages/AffiliateApplication';
import AffiliateDashboard from './pages/AffiliateDashboard';
import AffiliateManagement from './pages/AffiliateManagement';
import TenantMembers from './pages/TenantMembers';
import Analytics from './pages/Analytics';
import Events from './pages/Events';
import PublicEventsPage from './pages/PublicEventsPage';
import TicketCheckin from './pages/TicketCheckin';
import Popups from './pages/Popups';
import TrialBanner from './components/TrialBanner';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolves /article/:slug by looking up which tenant owns it, then redirects to /site/:siteSlug/article/:slug
const ArticleResolver = () => {
  const slug = window.location.pathname.replace(/^\/article\//, '');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!slug) { setError('No article slug'); return; }
    fetch(`${API_URL}/api/public/resolve-article/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(data => { window.location.replace(`/site/${data.site_slug}/article/${slug}`); })
      .catch(() => setError('Article not found'));
  }, [slug]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
        <p className="text-xl text-slate-600 mb-6">{error}</p>
        <a href="/" className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Go Home</a>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-slate-500">Loading article...</p>
    </div>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return user ? (
    <InactivityHandler>
      <TrialBanner />
      {children}
    </InactivityHandler>
  ) : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  // Resellers land on their portal dashboard, everyone else on the tenant dashboard.
  const home = user?.reseller_id ? '/reseller/dashboard' : '/dashboard';
  return user ? <Navigate to={home} /> : children;
};

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <SubdomainRouter>
            <div className="App">
              <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Analytics />
                </PrivateRoute>
              }
            />
            <Route
              path="/pages"
              element={
                <PrivateRoute>
                  <Pages />
                </PrivateRoute>
              }
            />
            <Route
              path="/pages/:id"
              element={
                <PrivateRoute>
                  <PageEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <PrivateRoute>
                  <Templates />
                </PrivateRoute>
              }
            />
            <Route
              path="/newsletters"
              element={
                <PrivateRoute>
                  <Newsletters />
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-assistant"
              element={
                <PrivateRoute>
                  <AiAssistant />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ai-bots"
              element={
                <PrivateRoute>
                  <AiBotsAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/taptile"
              element={
                <PrivateRoute>
                  <TaptileApp />
                </PrivateRoute>
              }
            />
            <Route
              path="/stripe-payments"
              element={
                <PrivateRoute>
                  <StripePayments />
                </PrivateRoute>
              }
            />
            <Route
              path="/newsletters/:id"
              element={
                <PrivateRoute>
                  <NewsletterEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/media"
              element={
                <PrivateRoute>
                  <MediaLibrary />
                </PrivateRoute>
              }
            />
            <Route
              path="/team"
              element={
                <PrivateRoute>
                  <Team />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization-members"
              element={
                <PrivateRoute>
                  <TenantMembers />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <UserManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <PrivateRoute>
                  <AuditLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/webhook-logs"
              element={
                <PrivateRoute>
                  <WebhookLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/go-live-requests"
              element={
                <PrivateRoute>
                  <GoLiveRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/theme-settings"
              element={
                <PrivateRoute>
                  <ThemeSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/domain-settings"
              element={
                <PrivateRoute>
                  <DomainSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/security-settings"
              element={
                <PrivateRoute>
                  <SecuritySettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <PrivateRoute>
                  <APIKeys />
                </PrivateRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <PrivateRoute>
                  <ComplianceDocuments />
                </PrivateRoute>
              }
            />
            <Route
              path="/help"
              element={
                <PrivateRoute>
                  <HelpTutorials />
                </PrivateRoute>
              }
            />
            <Route
              path="/navigation"
              element={
                <PrivateRoute>
                  <NavigationManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/redirects"
              element={
                <PrivateRoute>
                  <Redirects />
                </PrivateRoute>
              }
            />
            <Route
              path="/api-docs"
              element={
                <PrivateRoute>
                  <APIDocs />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms"
              element={
                <PrivateRoute>
                  <FormBuilder />
                </PrivateRoute>
              }
            />
            <Route
              path="/events"
              element={
                <PrivateRoute>
                  <Events />
                </PrivateRoute>
              }
            />
            <Route
              path="/popups"
              element={
                <PrivateRoute>
                  <Popups />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms/:id"
              element={
                <PrivateRoute>
                  <FormEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/forms/:id/submissions"
              element={
                <PrivateRoute>
                  <FormSubmissions />
                </PrivateRoute>
              }
            />
            {/* Articles Routes */}
            <Route
              path="/articles"
              element={
                <PrivateRoute>
                  <Articles />
                </PrivateRoute>
              }
            />
            <Route
              path="/articles/:id"
              element={
                <PrivateRoute>
                  <ArticleEditor />
                </PrivateRoute>
              }
            />
            {/* Public Preview Route - No auth required */}
            <Route path="/preview/article/:token" element={<ArticlePreviewPage />} />
            <Route path="/preview/:token" element={<PreviewPage />} />
            
            {/* Public Tenant Site - Path-based routing (Primary multi-tenant method) */}
            <Route path="/site/:siteSlug" element={<PublicSitePage />} />
            <Route path="/site/:siteSlug/events" element={<PublicEventsPage />} />
            <Route path="/site/:siteSlug/events/rsvp/:cancelToken" element={<PublicEventsPage />} />
            <Route path="/site/:siteSlug/events/waitlist/:waitlistToken" element={<PublicEventsPage />} />
            <Route path="/site/:siteSlug/events/:eventId" element={<PublicEventsPage />} />
            <Route path="/ticket-checkin/:qrToken" element={<TicketCheckin />} />
            <Route path="/site/:siteSlug/page/:pageSlug" element={<PublicSitePage />} />
            <Route path="/site/:siteSlug/article/:articleSlug" element={<PublicSitePage />} />
            <Route path="/article/*" element={<ArticleResolver />} />
            
            {/* Affiliate Pages */}
            <Route path="/affiliate/apply" element={<AffiliateApplication />} />
            <Route
              path="/affiliate/dashboard"
              element={
                <PrivateRoute>
                  <AffiliateDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/affiliates"
              element={
                <PrivateRoute>
                  <AffiliateManagement />
                </PrivateRoute>
              }
            />
            
            {/* Reseller Application Page (Public) */}
            <Route path="/become-a-reseller" element={<ResellerApplication />} />
            
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Payment Success Page */}
            <Route path="/payment-success" element={<PaymentSuccess />} />
            
            {/* Tenant Landing Page Editor (All authenticated users) */}
            <Route
              path="/landing-page"
              element={
                <PrivateRoute>
                  <TenantLandingPage />
                </PrivateRoute>
              }
            />
            
            {/* CMS Landing Page Editor (Super Admin only) */}
            <Route
              path="/cms/landing-page"
              element={
                <PrivateRoute>
                  <LandingPageEditor />
                </PrivateRoute>
              }
            />

            {/* Master Admin — configurable plan limits */}
            <Route
              path="/plan-limits"
              element={
                <PrivateRoute>
                  <PlanLimits />
                </PrivateRoute>
              }
            />

            {/* Marketplace — platform partners (visible to all tenants) */}
            <Route
              path="/marketplace"
              element={
                <PrivateRoute>
                  <Marketplace />
                </PrivateRoute>
              }
            />
            
            {/* Reseller Management (Platform Admin only) */}
            <Route
              path="/resellers"
              element={
                <PrivateRoute>
                  <ResellerManagement />
                </PrivateRoute>
              }
            />
            
            {/* Reseller Dashboard (For Reseller Admins) */}
            <Route
              path="/reseller/dashboard"
              element={
                <PrivateRoute>
                  <ResellerDashboard />
                </PrivateRoute>
              }
            />
            
            {/* Reseller Billing (For Reseller Admins) */}
            <Route
              path="/reseller/billing"
              element={
                <PrivateRoute>
                  <ResellerBilling />
                </PrivateRoute>
              }
            />
          </Routes>
          <Toaster 
            position="top-center" 
            duration={10000}
            closeButton={true}
            offset="40vh"
            toastOptions={{
              style: {
                cursor: 'pointer',
              },
              classNames: {
                error: '!bg-red-600 !text-white !border-red-700 !shadow-lg',
              },
            }}
          />
        </div>
        </SubdomainRouter>
      </BrowserRouter>
    </AuthProvider>
    </BrandingProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
